import { HttpErrorResponse } from "@angular/common/http";
import { AfterViewInit, Component, computed, effect, inject, linkedSignal, signal } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { ReactiveFormsModule } from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import { GrowToDirective } from "@kno2/common/directives";
import { DocumentTypesService } from "@kno2/data-access/document-types";
import { IntegrationsService } from "@kno2/data-access/integrations";
import { MessagesService, PatientIntegrationMeta } from "@kno2/data-access/messages";
import { ReleaseType } from "@kno2/data-access/release-types";
import { SettingsService } from "@kno2/data-access/settings";
import { MessageActionBarComponent, MessagePatientAttachmentsComponent, MessagePatientDetailsComponent } from "@kno2/features/message";
import { MessageFormManagerService, MessageFormService } from "@kno2/features/message/shared";
import { NotificationAlertComponent } from "@kno2/shared/notification-alert";
import { PdfViewerContainerComponent, PdfViewerOptions } from "@kno2/shared/pdf-viewer";
import { formatInTimeZone } from "date-fns-tz";
import { ReleaseAddressPanelComponent } from "./address-panel/release-address-panel.component";

@Component({
    selector: "kno2-release-response",
    imports: [
        ReactiveFormsModule,
        MessageActionBarComponent,
        MessagePatientDetailsComponent,
        MessagePatientAttachmentsComponent,
        ReleaseAddressPanelComponent,
        NotificationAlertComponent,
        PdfViewerContainerComponent,
        GrowToDirective
    ],
    templateUrl: "./release-response.component.html"
})
export class ReleaseResponseComponent implements AfterViewInit {
    private readonly route = inject(ActivatedRoute);
    private readonly router = inject(Router);
    private readonly messagesService = inject(MessagesService);
    private readonly messageFormService = inject(MessageFormService);
    private readonly messageFormManagerService = inject(MessageFormManagerService);
    private readonly documentTypesService = inject(DocumentTypesService);
    private readonly integrationsService = inject(IntegrationsService);
    private readonly settingsService = inject(SettingsService);

    public form = this.messageFormService.reset().form;
    public patientIntegrationMeta = signal<PatientIntegrationMeta>(null);
    public message = this.route.snapshot.data.message;
    public responseType: "reply" | "forward" = this.router.url.includes("/forward") ? "forward" : "reply";
    public attachments = this.responseType === "reply" ? [] : this.message.attachments;

    protected deliverySummaryEnableDownload = signal(false);
    protected showDocumentViewer = signal<boolean>(false);
    protected useIntegrationDocumentTypes = this.route.snapshot.data.useIntegrationDocumentTypes;

    protected error = this.messageFormManagerService.getError;
    protected serverError = computed(() => (this.integrationDocTypesResource.error() as HttpErrorResponse)?.error?.message || null);

    protected selectedAttachment = linkedSignal(() => this.message.attachments?.[0]);
    protected capabilities = this.integrationsService.getCapabilities();

    private hasFacilityLookup = computed(() => this.capabilities()?.capabilities?.some((cap) => cap.display === "facilityLookup"));
    private facilityIdControl = this.form.get("patient.facilityId");
    private facilityIdValue = toSignal(this.facilityIdControl.valueChanges, { initialValue: this.facilityIdControl.value });
    private defaultDocumentTypes = !this.useIntegrationDocumentTypes ? this.documentTypesService.getDocumentTypes() : null;

    private integrationDocTypesQuery = computed(() => {
        if (!this.useIntegrationDocumentTypes) return undefined;
        if (!this.capabilities()) return undefined;
        if (this.hasFacilityLookup()) {
            const facilityId = this.facilityIdValue();
            if (!facilityId) return undefined;
            return { facilityId: Number(facilityId) };
        }
        return {};
    });

    private integrationDocTypesResource = this.integrationsService.getIntegrationDocumentTypesResource(this.integrationDocTypesQuery);

    protected documentTypes = computed(() => {
        if (!this.useIntegrationDocumentTypes) return this.defaultDocumentTypes?.();
        if (!this.integrationDocTypesQuery()) return undefined;
        if (this.integrationDocTypesResource.error()) return [];
        return this.integrationDocTypesResource.value();
    });

    private readonly releaseTypeChanges = toSignal<ReleaseType>(this.form.get("releaseType").valueChanges);

    private releaseTypeChangesEffect = effect(() => {
        const releaseType = this.releaseTypeChanges();
        this.messageFormManagerService.applyReleaseType(releaseType);

        if (this.responseType === "forward") {
            this.form.get("subject").setValue(`FW: ${releaseType?.name || this.message.subject || ""}`);
        }
    });

    private readonly attachmentSendTypeChanges = toSignal(this.form.get("attachmentSendType").valueChanges);
    private attachmentSendTypeChangesEffect = effect(() => {
        const attachmentSendType = this.attachmentSendTypeChanges();
        this.messageFormService.toggleAttachmentDocumentTypeValidator(attachmentSendType === "CDA" || attachmentSendType === "PdfCda");
    });

    protected viewerOptions = signal<PdfViewerOptions>({
        rotate: "enabled",
        remove: "enabled",
        print: "hidden",
        download: "hidden",
        zoom: "auto"
    });

    public async ngAfterViewInit(): Promise<void> {
        this.messageFormService
            .setMessageDetails(this.message, this.responseType)
            .setBody(this.getMessageBody())
            .setPatientDetails(this.message.patient)
            .setAttachments(this.attachments)
            .addReleaseValidators();

        if (this.responseType === "reply") {
            this.messageFormService.addPatientValidators();
        }

        if (this.message?.patient?.integrationMeta) {
            this.patientIntegrationMeta.set(this.message.patient.integrationMeta);
        }

        this.clearError();
        const interoperabilitySettings = await this.settingsService.getInteroperabilitySettingsAsync();
        this.deliverySummaryEnableDownload.set(interoperabilitySettings.deliverySummaryEnableDownload);

        if (this.responseType === "forward") {
            const { id } = await this.messagesService.copyMessageAndAttachments(this.message.id);
            this.form.patchValue({
                id
            });
        }
    }

    public getMessageBody(): string {
        const date = new Date(this.message?.messageDate);
        const localDate = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
        const formattedDate = formatInTimeZone(localDate, Intl.DateTimeFormat().resolvedOptions().timeZone, "EEE, MMM d, yyyy 'at' h:mm a");
        const replyDateHeader = `On ${formattedDate}, ${this.message?.fromAddress} wrote:\r\n`;
        const divider = "\r\n\r\n----------\r\n";

        return [divider, replyDateHeader, this.message?.body].join("\r\n");
    }

    public clearError(): void {
        this.messageFormManagerService.clearError();
    }
}
